import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Plus, AlertTriangle, TrendingDown, CalendarClock, X } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, EmptyState } from "@/components/ui-kit";
import { Modal, L } from "@/pages/Protocols";
import api from "@/lib/api";

const KIND = {
  high: { bg: "bg-rose-50", color: "text-rose-600", Icon: AlertTriangle },
  warn: { bg: "bg-amber-50", color: "text-amber-600", Icon: TrendingDown },
  info: { bg: "bg-sky-50", color: "text-sky-600", Icon: CalendarClock },
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ kind: "warn", title: "", description: "" });

  const load = useCallback(() => api.get("/alerts").then((r) => setAlerts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (a) => { await api.put(`/alerts/${a.id}`, { resolved: true }); toast.success("Alert resolved"); load(); };
  const snooze = async (a) => { await api.put(`/alerts/${a.id}`, { snoozed: true }); toast("Alert snoozed"); load(); };
  const create = async () => {
    if (!form.title.trim()) return toast.error("Enter an alert title");
    await api.post("/alerts", form); toast.success("Alert created"); setModal(false);
    setForm({ kind: "warn", title: "", description: "" }); load();
  };

  return (
    <>
      <PageHead title="Alerts" subtitle="Clinical alerts that need your attention."
        action={<Btn variant="primary" data-testid="new-alert-button" onClick={() => setModal(true)}><Plus size={16} /> New Alert</Btn>} />
      <Card>
        {alerts.length === 0 ? (
          <EmptyState icon={<Bell size={24} />} title="No active alerts"
            subtitle="Clinical alerts (missed follow-ups, declining adherence, overdue reviews) will surface here." />
        ) : alerts.map((a) => {
          const k = KIND[a.kind] || KIND.info;
          return (
            <div key={a.id} className={`flex gap-4 items-start px-5 py-4 border-b border-slate-100 last:border-0 ${a.snoozed ? "opacity-50" : ""}`} data-testid={`alert-${a.id}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${k.bg} ${k.color}`}><k.Icon size={17} /></div>
              <div className="flex-1"><div className="font-semibold text-[13.5px]">{a.title}</div><div className="text-[12.5px] text-slate-500 mt-0.5">{a.description}</div></div>
              <div className="flex gap-2 shrink-0">
                <Btn size="sm" onClick={() => snooze(a)} data-testid={`alert-snooze-${a.id}`}>Snooze</Btn>
                <Btn size="sm" className="text-teal-700 border-teal-300" onClick={() => resolve(a)} data-testid={`alert-resolve-${a.id}`}>Resolve</Btn>
              </div>
            </div>
          );
        })}
      </Card>
      {modal && (
        <Modal title="Create Alert" onClose={() => setModal(false)}>
          <L label="Type"><select className="pq-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}><option value="high">High Risk</option><option value="warn">Warning</option><option value="info">Info</option></select></L>
          <L label="Title"><input className="pq-input" data-testid="alert-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Missed follow-up" /></L>
          <L label="Description"><textarea className="pq-input min-h-[70px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></L>
          <div className="flex justify-end gap-2 mt-4"><Btn onClick={() => setModal(false)}>Cancel</Btn><Btn variant="primary" data-testid="create-alert-submit" onClick={create}>Create</Btn></div>
        </Modal>
      )}
    </>
  );
}
